/**
 * JobFilterBar component for Jobs page.
 * Uses FilterCombobox and MultiSelectCombobox from shared library.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button, FilterCombobox, MultiSelectCombobox, useAuth } from '@aaas/shared';
import type { FilterOption } from '@aaas/shared';
import { usePositionListQuery } from '../hooks/usePositionQueries';
import { useCampaignListQuery } from '../hooks/useCampaignQueries';
import { usePipelineListQuery } from '../hooks/usePipelineQueries';
import { JOB_STATUS_CONFIG, type JobStatus } from '../types';

export interface JobFilters {
  status?: string;
  positionIds?: string[];
  campaignIds?: string[];
  pipelineId?: string;
}

interface JobFilterBarProps {
  filters: JobFilters;
  onFiltersChange: (filters: JobFilters) => void;
}

export function JobFilterBar({ filters, onFiltersChange }: JobFilterBarProps) {
  const { currentWorkspace, idToken } = useAuth();
  const { t } = useTranslation('ats');

  // Fetch filter options
  const { data: positionResponse, isLoading: positionsLoading } = usePositionListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page: 1,
    size: 100,
    enabled: !!currentWorkspace && !!idToken,
    refetchInterval: false,
  });

  const { data: campaignResponse, isLoading: campaignsLoading } = useCampaignListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page: 1,
    size: 100,
    enabled: !!currentWorkspace && !!idToken,
    refetchInterval: false,
  });

  const { data: pipelineResponse, isLoading: pipelinesLoading } = usePipelineListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page: 1,
    size: 100,
    enabled: !!currentWorkspace && !!idToken,
    refetchInterval: false,
  });

  // Transform data to FilterOption format (memoized to prevent unnecessary recalculations)
  const statusOptions = useMemo<FilterOption[]>(
    () =>
      (Object.keys(JOB_STATUS_CONFIG) as JobStatus[]).map((status) => ({
        value: status,
        label: JOB_STATUS_CONFIG[status].label,
      })),
    []
  );

  const positionOptions = useMemo<FilterOption[]>(
    () =>
      (positionResponse?.data?.items || []).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [positionResponse]
  );

  const campaignOptions = useMemo<FilterOption[]>(
    () =>
      (campaignResponse?.data?.items || []).map((c) => ({
        value: c.id,
        label: c.name,
      })),
    [campaignResponse]
  );

  const pipelineOptions = useMemo<FilterOption[]>(
    () =>
      (pipelineResponse?.data?.items || []).map((p) => ({
        value: p.id,
        label: p.name,
      })),
    [pipelineResponse]
  );

  const hasActiveFilters =
    filters.status ||
    (filters.positionIds && filters.positionIds.length > 0) ||
    (filters.campaignIds && filters.campaignIds.length > 0) ||
    filters.pipelineId;

  const handleClearFilters = () => {
    onFiltersChange({});
  };

  // Update single-value filter (status, pipelineId)
  const updateFilter = (key: 'status' | 'pipelineId', value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value,
    });
  };

  // Update multi-value filter (positionIds, campaignIds)
  const updateArrayFilter = (key: 'positionIds' | 'campaignIds', values: string[]) => {
    onFiltersChange({
      ...filters,
      [key]: values.length > 0 ? values : undefined,
    });
  };

  // Count active filters (arrays count as 1 if they have items)
  const activeFilterCount = [
    filters.status,
    filters.positionIds && filters.positionIds.length > 0,
    filters.campaignIds && filters.campaignIds.length > 0,
    filters.pipelineId,
  ].filter(Boolean).length;

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-gray-50 rounded-lg border">
      {/* Status Filter */}
      <FilterCombobox
        options={statusOptions}
        value={filters.status}
        onChange={(value) => updateFilter('status', value)}
        placeholder={t('jobFilter.status')}
        searchPlaceholder={t('jobFilter.statusSearch')}
        emptyMessage={t('jobFilter.statusEmpty')}
        className="w-[140px]"
      />

      {/* Position Filter (Multi-select) */}
      <MultiSelectCombobox
        options={positionOptions}
        values={filters.positionIds || []}
        onChange={(values) => updateArrayFilter('positionIds', values)}
        placeholder={t('jobFilter.position')}
        displayName={t('jobFilter.positionDisplay')}
        searchPlaceholder={t('jobFilter.positionSearch')}
        emptyMessage={t('jobFilter.positionEmpty')}
        isLoading={positionsLoading}
        className="w-[180px]"
      />

      {/* Campaign Filter (Multi-select) */}
      <MultiSelectCombobox
        options={campaignOptions}
        values={filters.campaignIds || []}
        onChange={(values) => updateArrayFilter('campaignIds', values)}
        placeholder={t('jobFilter.campaign')}
        displayName={t('jobFilter.campaignDisplay')}
        searchPlaceholder={t('jobFilter.campaignSearch')}
        emptyMessage={t('jobFilter.campaignEmpty')}
        isLoading={campaignsLoading}
        className="w-[180px]"
      />

      {/* Pipeline Filter */}
      <FilterCombobox
        options={pipelineOptions}
        value={filters.pipelineId}
        onChange={(value) => updateFilter('pipelineId', value)}
        placeholder={t('jobFilter.pipeline')}
        searchPlaceholder={t('jobFilter.pipelineSearch')}
        emptyMessage={t('jobFilter.pipelineEmpty')}
        isLoading={pipelinesLoading}
        className="w-[180px]"
      />

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="h-4 w-4 mr-1" />
          {t('jobFilter.clear', { count: activeFilterCount })}
        </Button>
      )}
    </div>
  );
}
